import { formatISO, isAfter, isBefore, isPast, isSameMonth, parseISO } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { useManualFormValues } from '@asteria/component-form';

export function filterOperations(operations = [], filters) {
	return (operations ?? []).filter(Boolean).filter((operation) => {
		if (operation.op === 'REMOVE') {
			return false;
		}

		if (filters.startDate && filters.endDate) {
			const paymentDate = parseISO(operation.payload.paymentDate);
			const startDate = parseISO(filters.startDate);
			const endDate = parseISO(filters.endDate);

			if (
				!isSameMonth(paymentDate, startDate) &&
				(isBefore(paymentDate, startDate) ||
					isAfter(paymentDate, endDate))
			) {
				return false;
			}
		}

		if (
			filters.type &&
			operation.payload.type &&
			operation.payload.type.toLowerCase() !== filters.type.toLowerCase()
		) {
			return false;
		}

		if (
			filters.types &&
			operation.payload.type &&
			!filters.types.includes(operation.payload.type.toLowerCase())
		) {
			return false;
		}

		if (
			filters.tagId &&
			operation.payload.tags &&
			!operation.payload.tags.includes(filters.tagId)
		) {
			return false;
		}

		return true;
	});
}

export function mergeOperations(operations = []) {
	let transactions = [];

	for (let i = 0; i < operations.length; i++) {
		const operation = operations[i];

		if (operation.op === 'CREATE') {
			transactions.push(operation);
		} else if (operation.op === 'REPLACE') {
			const previousOperation = transactions.find(
				(op) =>
					op._id === operation.payload.targetId ||
					op.uuid === operation.payload.targetId,
			);

			if (previousOperation) {
				transactions = transactions.filter(
					(op) => op._id !== previousOperation._id,
				);

				transactions.push({
					...previousOperation,
					payload: {
						...previousOperation.payload,
						...operation.payload,
					},
				});
			}
		} else if (operation.op === 'REMOVE') {
			transactions = transactions.filter(
				(op) =>
					op._id !== operation.payload.targetId &&
					op.uuid !== operation.payload.targetId,
			);
		}
	}

	return transactions;
}

export function updateOperations({
	value: $value,
	date,
	type,
	tagId,
	defaultTagId,
	operations,
}) {
	const value = parseFloat($value);

	// Prioritize create operations
	const { operationsToRemove, operationsToModify, sum } = operations.reduce(
		(acc, op) => {
			if (acc.sum - op.payload.total < 0) {
				if (!acc.operationsToModify) {
					acc.operationsToModify = op;
					acc.sum -= op.payload.total;
				} else {
					acc.operationsToRemove.push(op);
				}

				return acc;
			}

			acc.operationsToModify = op;
			acc.sum -= op.payload.total;

			return acc;
		},
		{
			operationsToRemove: [],
			operationsToModify: null,
			sum: value,
		},
	);

	// Mark operations to remove
	const changes = operationsToRemove.map((op) => ({
		op: 'REMOVE',
		payload: {
			targetId: op._id,
		},
	}));

	// If we have a modify operation, we need to update the total
	if (operationsToModify) {
		if (
			operationsToModify.op === 'REPLACE' ||
			operationsToModify.op === 'REMOVE'
		) {
			// Update existing modify operation
			// operationsToModify.payload.total += sum;
			changes.push({
				...operationsToModify,
				op: value !== 0 ? 'REPLACE' : 'REMOVE',
				payload: {
					...operationsToModify.payload,
					total: operationsToModify.payload.total + sum,
				},
			});
		} else if (value !== 0 && sum !== 0) {
			// Create new modify operation
			changes.push({
				op: 'REPLACE',
				payload: {
					...operationsToModify.payload,
					total: operationsToModify.payload.total + sum,
					targetId: operationsToModify.uuid ?? operationsToModify._id,
				},
			});
		} else if (value === 0 && sum !== 0) {
			// Remove the operation if the value is 0
			changes.push({
				op: 'REMOVE',
				payload: {
					targetId: operationsToModify.uuid ?? operationsToModify._id,
				},
			});
		}
	} else {
		const uuid = uuidv4();
		changes.push({
			uuid,
			op: 'CREATE',
			payload: {
				total: sum,
				paymentDate: formatISO(isPast(date) ? new Date() : date, {
					representation: 'date',
				}),
				currency: 'SEK',
				tags: [tagId ?? defaultTagId],
				type: type.toUpperCase(),
			},
		});
	}

	return changes;
}

export function getTransactions(filters, { getValues, includeIntermediate }) {
	const operations = getValues('allOperations') ?? [];

	const intermediateOperations = getValues('intermediateChanges') ?? [];

	return filterOperations(
		mergeOperations(
			includeIntermediate
				? operations.concat(intermediateOperations)
				: operations,
		),
		filters,
	);
}

export function useTransactions(filters, options = {}) {
	const operations =
		useManualFormValues({
			name: 'allOperations',
		}) ?? [];

	const intermediateOperations =
		useManualFormValues({
			name: 'intermediateChanges',
		}) ?? [];

	return filterOperations(
		mergeOperations(
			options?.includeIntermediate
				? operations.concat(intermediateOperations)
				: operations,
		),
		filters,
	);
}
